import { activeCameraIdAtom } from "@/atoms/activeCamera";
import { activeRegionAtom } from "@/atoms/activeRegion";
import { useRegion } from "@/hooks/useRegions";
import { trackWindMapScrub } from "@/lib/analytics";
import { createBasemapLayer } from "@/lib/mapTiles";
import { MapBounds, useWindData, VelocityDatum } from "@/lib/wind";
import {
  Box,
  CircularProgress,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { addHours, format } from "date-fns";
import L from "leaflet";
import "leaflet-velocity";
import "leaflet-velocity/dist/leaflet-velocity.css";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { createWindSpeedOverlay } from "./WindSpeedOverlay";

// leaflet-velocity expects L on window
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).L = L;

declare module "leaflet" {
  function velocityLayer(options: Record<string, unknown>): L.Layer;
}

const STEP_HOURS = 3;
const MAX_STEPS = 40; // 0, 3, 6, … 117, 120 (5 days)

function getMapBounds(map: L.Map): MapBounds {
  const b = map.getBounds();
  const padLat = (b.getNorth() - b.getSouth()) * 0.3;
  const padLng = (b.getEast() - b.getWest()) * 0.3;
  return {
    north: b.getNorth() + padLat,
    south: b.getSouth() - padLat,
    east: b.getEast() + padLng,
    west: b.getWest() - padLng,
  };
}

function roundBounds(bounds: MapBounds): MapBounds {
  return {
    north: Math.ceil(bounds.north),
    south: Math.floor(bounds.south),
    east: Math.ceil(bounds.east),
    west: Math.floor(bounds.west),
  };
}

function getTimeLabel(hour: number): string {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const target = addHours(now, hour);
  if (hour === 0) return "Now";
  return format(target, "EEE ha");
}

function getSliderMarks(compact: boolean) {
  const marks = [];
  // Show text labels every 12h (every 4th step) to avoid crowding
  const labelEvery = compact ? 8 : 4; // mobile: every 24h, desktop: every 12h
  for (let i = 0; i <= MAX_STEPS; i++) {
    marks.push({
      value: i,
      label: i % labelEvery === 0 ? getTimeLabel(i * STEP_HOURS) : "",
    });
  }
  return marks;
}

function addBreakMarkers(
  map: L.Map,
  breaks: { name: string; lat: number; lon: number }[],
): L.LayerGroup {
  const group = L.layerGroup();
  breaks.forEach((b, i) => {
    const isMain = i === 0;
    const marker = L.circleMarker([b.lat, b.lon], {
      radius: isMain ? 6 : 4,
      fillColor: isMain ? "#4FC3F7" : "#66BB6A",
      fillOpacity: isMain ? 0.9 : 0.6,
      color: "rgba(255,255,255,0.8)",
      weight: isMain ? 2 : 1,
    });
    marker.bindTooltip(b.name, {
      className: "wind-map-tooltip",
      direction: "top",
      offset: [0, -6],
    });
    group.addLayer(marker);
  });
  group.addTo(map);
  return group;
}

export default function WindMap() {
  const activeRegion = useRecoilValue(activeRegionAtom);
  const activeCameraId = useRecoilValue(activeCameraIdAtom);
  const { data: regionData } = useRegion(activeRegion);

  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const forecastHour = stepIndex * STEP_HOURS;

  const { data, isFetching } = useWindData(
    bounds ? roundBounds(bounds) : null,
    forecastHour,
  );

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const velocityLayerRef = useRef<L.Layer | null>(null);
  const overlayRef = useRef<L.ImageOverlay | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const currentRegionRef = useRef(activeRegion);
  const currentBreakRef = useRef(activeCameraId);

  const updateBounds = useCallback(() => {
    if (mapRef.current) {
      setBounds(getMapBounds(mapRef.current));
    }
  }, []);

  // Initialize map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const firstBreak = regionData?.breaks[0];
    const initLat = firstBreak?.lat ?? 0;
    const initLon = firstBreak?.lon ?? 0;

    const map = L.map(mapContainerRef.current, {
      center: [initLat, initLon],
      zoom: 12,
      zoomControl: true,
      attributionControl: true,
    });

    createBasemapLayer("dark").addTo(map);

    if (regionData) {
      markersRef.current = addBreakMarkers(map, regionData.breaks);
    }

    mapRef.current = map;
    setBounds(getMapBounds(map));
    map.on("moveend", updateBounds);

    return () => {
      map.off("moveend", updateBounds);
      map.remove();
      mapRef.current = null;
      markersRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Map init runs once on mount
  }, []);

  // Fly to new spot when region or break changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !regionData) return;

    const regionChanged = currentRegionRef.current !== activeRegion;
    const breakChanged = currentBreakRef.current !== activeCameraId;
    if (!regionChanged && !breakChanged) return;

    currentRegionRef.current = activeRegion;
    currentBreakRef.current = activeCameraId;

    // Find the active break, fall back to first break
    const activeBreak = activeCameraId
      ? regionData.breaks.find((b) => b.id === activeCameraId)
      : null;
    const targetBreak = activeBreak ?? regionData.breaks[0];
    if (!targetBreak) return;

    map.flyTo([targetBreak.lat, targetBreak.lon], 12, { duration: 1.2 });

    // Replace markers on region change
    if (regionChanged) {
      if (markersRef.current) {
        map.removeLayer(markersRef.current);
      }
      markersRef.current = addBreakMarkers(map, regionData.breaks);
    }
  }, [activeRegion, activeCameraId, regionData]);

  // Update wind overlay and velocity layers
  useEffect(() => {
    if (!mapRef.current || !data) return;

    if (overlayRef.current) {
      mapRef.current.removeLayer(overlayRef.current);
      overlayRef.current = null;
    }
    if (velocityLayerRef.current) {
      mapRef.current.removeLayer(velocityLayerRef.current);
      velocityLayerRef.current = null;
    }

    const heatmap = createWindSpeedOverlay(data);
    if (heatmap) {
      heatmap.addTo(mapRef.current);
      overlayRef.current = heatmap;
    }

    const layer = L.velocityLayer({
      displayValues: true,
      displayOptions: {
        velocityType: "Wind",
        position: "bottomleft",
        emptyString: "",
        speedUnit: "m/s",
        angleConvention: "meteo",
      },
      data: data as VelocityDatum[],
      maxVelocity: 15,
      velocityScale: 0.01,
      colorScale: [
        "rgba(255,255,255,0.4)",
        "rgba(255,255,255,0.5)",
        "rgba(255,255,255,0.6)",
        "rgba(255,255,255,0.7)",
        "rgba(255,255,255,0.8)",
        "rgba(255,255,255,0.9)",
        "rgba(255,255,255,1.0)",
      ],
      particleAge: 64,
      lineWidth: 1.5,
      particleMultiplier: 1 / 300,
      frameRate: 15,
    });

    layer.addTo(mapRef.current);
    velocityLayerRef.current = layer;
  }, [data]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sliderMarks = useMemo(() => getSliderMarks(isMobile), [isMobile]);

  const activeBreakName = activeCameraId
    ? regionData?.breaks.find((b) => b.id === activeCameraId)?.name
    : null;
  const label = activeBreakName ?? regionData?.label ?? activeRegion;

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        mt: 3,
        position: "relative",
        border: "1px solid rgba(255,255,255,0.06)",
        backgroundColor: "background.paper",
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography
          variant="overline"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            display: "block",
            fontSize: "0.7rem",
            letterSpacing: "0.1em",
            color: "text.secondary",
          }}
        >
          Wind Map — {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "text.secondary",
            fontSize: "0.8rem",
          }}
        >
          {getTimeLabel(forecastHour)}
        </Typography>
      </Box>
      <Box
        ref={mapContainerRef}
        sx={{
          height: { xs: 300, sm: 400, md: 500 },
          width: "100%",
          "& .wind-map-dark-basemap": {
            filter: "grayscale(1) invert(1) brightness(0.8)",
          },
        }}
      />
      {/* Forecast time selector */}
      <Box
        sx={{ px: { xs: 2, md: 4 }, pb: 2, pt: 1, position: "relative", zIndex: 1000 }}
      >
        <Slider
          value={stepIndex}
          min={0}
          max={MAX_STEPS}
          step={1}
          marks={sliderMarks}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => getTimeLabel(v * STEP_HOURS)}
          onChangeCommitted={(_, value) =>
            trackWindMapScrub((value as number) * STEP_HOURS)
          }
          onChange={(_, value) => setStepIndex(value as number)}
          sx={{
            color: "primary.main",
            "& .MuiSlider-markLabel": {
              fontSize: { xs: "0.6rem", sm: "0.65rem" },
              color: "text.secondary",
              whiteSpace: "nowrap",
            },
          }}
        />
      </Box>
      {isFetching && (
        <Box
          sx={{
            position: "absolute",
            top: 60,
            right: 12,
            zIndex: 1000,
            bgcolor: "rgba(10,22,40,0.8)",
            borderRadius: "50%",
            p: 0.5,
            display: "flex",
          }}
        >
          <CircularProgress size={20} sx={{ color: "primary.main" }} />
        </Box>
      )}
    </Box>
  );
}
